import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FinancialCoreApi from "../../../../infrastructure/api/financialCore";
import LendingOperationsApi from "../../../../infrastructure/api/lendingOperations";
import MehrsanOperationsApi from "../../../../infrastructure/api/mehrsanOperations";
import RequestInternalNotesApi from "../../../../infrastructure/api/requestInternalNotes";
import { toJalaliFullDate } from "../../../middleware/middleware";
import MehrsanLogAttachment from "./MehrsanLogAttachment";
import { isCaseWorkspaceOperationsApi, resolveOperationsSource } from "./mehrsanOperationsConstants";
import { getEntityId } from "./mehrsanOperationsUtils";
import { buildActivityFeed } from "./requestActivityFeedUtils";

const normalizeList = (res) => {
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

const DETAIL_BATCH_SIZE = 4;

const fetchProcessDetails = async (list, getById, operationsSource) => {
  const results = [];
  for (let i = 0; i < list.length; i += DETAIL_BATCH_SIZE) {
    const chunk = list.slice(i, i + DETAIL_BATCH_SIZE);
    const chunkResults = await Promise.all(
      chunk.map(async (p) => {
        const id = getEntityId(p);
        if (!id) return { ...p, logs: [] };
        try {
          const res = await getById(operationsSource, id);
          const data = res?.data ?? res?.Data;
          if (res?.isSuccess === false || !data) return { ...p, logs: [] };
          return data;
        } catch {
          return { ...p, logs: [] };
        }
      })
    );
    results.push(...chunkResults);
  }
  return results;
};

const CATEGORY_ICONS = {
  workflow: SwapHorizOutlinedIcon,
  followup: ContactPhoneOutlinedIcon,
  mehrsan: VerifiedOutlinedIcon,
  lending: AccountBalanceOutlinedIcon,
  financial: PaymentsOutlinedIcon,
  assignment: AssignmentIndOutlinedIcon,
  internal_note: StickyNote2OutlinedIcon,
};

const RequestActivityFeedTab = ({
  requestInfoId,
  detailApi,
  fileBaseUrl = "",
  active = false,
  history = [],
  followUps = [],
  internalNotesRefreshKey = 0,
}) => {
  const operationsSource = resolveOperationsSource(detailApi);
  const canLoadOps = isCaseWorkspaceOperationsApi(detailApi);

  const [opsLoading, setOpsLoading] = useState(false);
  const [mehrsanError, setMehrsanError] = useState(null);
  const [lendingError, setLendingError] = useState(null);
  const [mehrsanProcesses, setMehrsanProcesses] = useState([]);
  const [lendingProcesses, setLendingProcesses] = useState([]);
  const [internalNotes, setInternalNotes] = useState([]);
  const [internalNotesError, setInternalNotesError] = useState(null);
  const [financialTransactions, setFinancialTransactions] = useState([]);
  const [financialError, setFinancialError] = useState(null);
  const loadedOpsKeyRef = useRef(null);
  const loadingRef = useRef(false);

  const loadMehrsanLogs = useCallback(async () => {
    setMehrsanError(null);
    try {
      const listRes = await MehrsanOperationsApi.getByRequestId(
        operationsSource,
        requestInfoId
      ).catch(() => null);

      if (!listRes || listRes.isSuccess === false) {
        setMehrsanError("بارگذاری فعالیت‌های مهرسان ناموفق بود.");
        setMehrsanProcesses([]);
        return;
      }

      const list = normalizeList(listRes);
      const details = await fetchProcessDetails(
        list,
        MehrsanOperationsApi.getById.bind(MehrsanOperationsApi),
        operationsSource
      );
      setMehrsanProcesses(details);
    } catch {
      setMehrsanError("بارگذاری فعالیت‌های مهرسان ناموفق بود.");
      setMehrsanProcesses([]);
    }
  }, [operationsSource, requestInfoId]);

  const loadLendingLogs = useCallback(async () => {
    setLendingError(null);
    try {
      const listRes = await LendingOperationsApi.getByRequestId(
        operationsSource,
        requestInfoId
      ).catch(() => null);

      if (!listRes || listRes.isSuccess === false) {
        setLendingError("بارگذاری فعالیت‌های وام ناموفق بود.");
        setLendingProcesses([]);
        return;
      }

      const list = normalizeList(listRes);
      const details = await fetchProcessDetails(
        list,
        LendingOperationsApi.getById.bind(LendingOperationsApi),
        operationsSource
      );
      setLendingProcesses(details);
    } catch {
      setLendingError("بارگذاری فعالیت‌های وام ناموفق بود.");
      setLendingProcesses([]);
    }
  }, [operationsSource, requestInfoId]);

  const loadFinancialTransactions = useCallback(async () => {
    setFinancialError(null);
    try {
      const res = await FinancialCoreApi.getTransactionsByRequestId(
        operationsSource,
        requestInfoId
      ).catch(() => null);

      if (!res || res.isSuccess === false) {
        setFinancialError("بارگذاری فعالیت‌های مالی ناموفق بود.");
        setFinancialTransactions([]);
        return;
      }

      setFinancialTransactions(normalizeList(res));
    } catch {
      setFinancialError("بارگذاری فعالیت‌های مالی ناموفق بود.");
      setFinancialTransactions([]);
    }
  }, [operationsSource, requestInfoId]);

  const loadInternalNotes = useCallback(async () => {
    if (!requestInfoId || !canLoadOps) return;
    setInternalNotesError(null);
    try {
      const res = await RequestInternalNotesApi.getByRequestId(
        operationsSource,
        requestInfoId
      );
      if (!res || res.isSuccess === false) {
        setInternalNotesError("بارگذاری یادداشت‌های داخلی در فید ناموفق بود.");
        setInternalNotes([]);
        return;
      }
      const data = res?.data ?? res?.Data;
      setInternalNotes(Array.isArray(data) ? data : []);
    } catch {
      setInternalNotesError("بارگذاری یادداشت‌های داخلی در فید ناموفق بود.");
      setInternalNotes([]);
    }
  }, [canLoadOps, operationsSource, requestInfoId]);

  const loadOperationsLogs = useCallback(async () => {
    if (!requestInfoId || !canLoadOps) return;

    const key = `${operationsSource}:${requestInfoId}`;
    if (loadedOpsKeyRef.current === key || loadingRef.current) return;

    loadingRef.current = true;
    setOpsLoading(true);
    setMehrsanError(null);
    setLendingError(null);

    await Promise.all([
      loadMehrsanLogs(),
      loadLendingLogs(),
      loadFinancialTransactions(),
      loadInternalNotes(),
    ]);

    loadedOpsKeyRef.current = key;
    loadingRef.current = false;
    setOpsLoading(false);
  }, [
    requestInfoId,
    canLoadOps,
    operationsSource,
    loadMehrsanLogs,
    loadLendingLogs,
    loadInternalNotes,
    loadFinancialTransactions,
  ]);

  useEffect(() => {
    if (!active) return;
    if (canLoadOps) loadOperationsLogs();
  }, [active, canLoadOps, loadOperationsLogs]);

  useEffect(() => {
    if (!active || !canLoadOps) return;
    if (loadedOpsKeyRef.current) loadInternalNotes();
  }, [active, canLoadOps, internalNotesRefreshKey, loadInternalNotes]);

  useEffect(() => {
    loadedOpsKeyRef.current = null;
    loadingRef.current = false;
    setMehrsanProcesses([]);
    setLendingProcesses([]);
    setMehrsanError(null);
    setLendingError(null);
    setInternalNotes([]);
    setInternalNotesError(null);
    setFinancialTransactions([]);
    setFinancialError(null);
  }, [requestInfoId, operationsSource]);

  const activities = useMemo(
    () =>
      buildActivityFeed({
        history,
        followUps,
        mehrsanProcesses: canLoadOps ? mehrsanProcesses : [],
        lendingProcesses: canLoadOps ? lendingProcesses : [],
        internalNotes: canLoadOps ? internalNotes : [],
        financialTransactions: canLoadOps ? financialTransactions : [],
      }),
    [
      history,
      followUps,
      mehrsanProcesses,
      lendingProcesses,
      internalNotes,
      financialTransactions,
      canLoadOps,
    ]
  );

  const hasLocalActivities = useMemo(
    () =>
      buildActivityFeed({
        history,
        followUps,
        mehrsanProcesses: [],
        lendingProcesses: [],
        internalNotes: canLoadOps ? internalNotes : [],
        financialTransactions: canLoadOps ? financialTransactions : [],
      }).length > 0,
    [history, followUps, internalNotes, financialTransactions, canLoadOps]
  );

  if (opsLoading && !activities.length && !hasLocalActivities) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {mehrsanError ? (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={loadMehrsanLogs}>
              تلاش مجدد
            </Button>
          }
        >
          {mehrsanError}
        </Alert>
      ) : null}

      {financialError ? (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={loadFinancialTransactions}>
              تلاش مجدد
            </Button>
          }
        >
          {financialError}
        </Alert>
      ) : null}

      {internalNotesError ? (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={loadInternalNotes}>
              تلاش مجدد
            </Button>
          }
        >
          {internalNotesError}
        </Alert>
      ) : null}

      {lendingError ? (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={loadLendingLogs}>
              تلاش مجدد
            </Button>
          }
        >
          {lendingError}
        </Alert>
      ) : null}

      {opsLoading ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            در حال بارگذاری فعالیت‌های پرونده...
          </Typography>
        </Box>
      ) : null}

      {!activities.length && !opsLoading ? (
        <Alert severity="info">
          هنوز فعالیتی برای این پرونده ثبت نشده است.
        </Alert>
      ) : (
        <Stack spacing={1.25} divider={<Divider flexItem />}>
          {activities.map((item) => {
            const Icon = CATEGORY_ICONS[item.category] || DescriptionOutlinedIcon;
            return (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 1.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box
                    sx={{
                      mt: 0.25,
                      color: "primary.main",
                      display: "flex",
                      flexShrink: 0,
                    }}
                  >
                    <Icon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      sx={{ mb: 0.5, gap: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={item.sourceLabel}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                      <Chip
                        size="small"
                        label={item.title}
                        color={item.chipColor}
                        variant={item.chipColor === "default" ? "outlined" : "filled"}
                        sx={{ height: 22, maxWidth: "100%" }}
                      />
                    </Stack>

                    <Typography variant="caption" color="text.secondary" component="div">
                      {toJalaliFullDate(item.occurredAt) || "—"}
                      {item.authorName ? ` · ${item.authorName}` : ""}
                    </Typography>

                    {item.description ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.75,
                          fontSize: "0.8rem",
                          lineHeight: 1.5,
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.description}
                      </Typography>
                    ) : null}

                    {item.attachmentUrl ? (
                      <Box sx={{ mt: 0.75 }}>
                        <MehrsanLogAttachment
                          url={item.attachmentUrl}
                          fileBaseUrl={fileBaseUrl}
                          compact
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

RequestActivityFeedTab.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detailApi: PropTypes.string,
  fileBaseUrl: PropTypes.string,
  active: PropTypes.bool,
  history: PropTypes.array,
  followUps: PropTypes.array,
  internalNotesRefreshKey: PropTypes.number,
};

export default RequestActivityFeedTab;
